import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { addDays, format, parseISO } from "date-fns";
import { api, formatApiError } from "@/lib/api";
import { FREQUENCIES } from "@/lib/constants";
import { SERVICES, TAX_YEARS, datesFor, hasTemplateDates, taxYearEnd } from "@/lib/services";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import TaskPlanEditor, { cleanPlan, planFromTemplate, planTotal } from "@/components/jobs/TaskPlanEditor";

const field = "rounded-xl bg-white border-[#103080]/20 h-10";

function Pick({ value, onChange, options, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={field} data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`${testId}-${o.value}`}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function ServiceForm({ svc, v, set, directory, templates, roles, defaultAssignee, rule }) {
  const tplOf = (id) => templates.find((t) => t.id === id);
  const tpl = tplOf(v.template_id);
  const tplDates = hasTemplateDates(tpl);
  const hasRule = Boolean(rule && (rule.months || rule.days)) || tplDates;
  const id = svc.key;
  const hasPlan = v.task_plan?.length > 0;
  const setPeriod = (patch) => set({ ...patch, ...datesFor(svc, patch.period_end, rule, tpl) });
  const setPlan = (patch) => set({ ...patch, ...("template_id" in patch && v.period_end ? { lead: svc.lead, ...datesFor(svc, v.period_end, rule, tplOf(patch.template_id)) } : {}) });
  const startDate = v.due && v.lead !== "" ? format(addDays(parseISO(v.due), -Number(v.lead)), "d MMM yyyy") : null;
  return (
    <div className="grid sm:grid-cols-2 gap-3 pt-4" data-testid={`onboard-form-${id}`}>
      {svc.freqOptions && (
        <div className="space-y-1.5">
          <Label>How often?</Label>
          <Pick value={v.frequency} onChange={(x) => set({ frequency: x })} testId={`onboard-${id}-frequency`}
            options={FREQUENCIES.filter((f) => svc.freqOptions.includes(f.value))} />
        </div>
      )}
      {svc.taxYear ? (
        <div className="space-y-1.5">
          <Label>Which tax year needs doing first?</Label>
          <Pick value={v.tax_year} onChange={(x) => setPeriod({ tax_year: x, period_end: taxYearEnd(x) })}
            testId={`onboard-${id}-tax-year`} options={TAX_YEARS.map((t) => ({ value: t, label: t }))} />
        </div>
      ) : (
        <div className="space-y-1.5">
          <Label>{svc.periodLabel}</Label>
          <Input type="date" required value={v.period_end} className={field} data-testid={`onboard-${id}-period-end`}
            onChange={(e) => setPeriod({ period_end: e.target.value })} />
        </div>
      )}
      {!svc.hideDue && (
        <div className="space-y-1.5">
          <Label>{hasRule && !svc.noStatutory ? "Internal deadline" : svc.dueLabel}</Label>
          <Input type="date" required value={v.due} onChange={(e) => set({ due: e.target.value })} className={field} data-testid={`onboard-${id}-due`} />
          {hasRule && !svc.noStatutory && v.statutory && <p className="text-[11px] text-[#0F172A]/55" data-testid={`onboard-${id}-statutory`}>{svc.dueLabel}: <b>{format(parseISO(v.statutory), "d MMM yyyy")}</b> (statutory)</p>}
          {tplDates && <p className="text-[11px] text-[#0090D0]" data-testid={`onboard-${id}-template-dates`}>Dates from the template "{tpl.name}"</p>}
        </div>
      )}
      {!hasPlan && (
      <div className="space-y-1.5">
        <Label>Estimated time (minutes)</Label>
        <Input type="number" min={5} step={5} required value={v.duration} onChange={(e) => set({ duration: e.target.value })} className={field} data-testid={`onboard-${id}-duration`} />
      </div>
      )}
      <div className="space-y-1.5">
        <Label>Card appears (days before due)</Label>
        <Input type="number" min={0} max={365} required value={v.lead} onChange={(e) => set({ lead: e.target.value })} className={field} data-testid={`onboard-${id}-lead`} />
        {startDate && <p className="text-[11px] text-[#0F172A]/55" data-testid={`onboard-${id}-start-date`}>Start date: <b>{startDate}</b></p>}
      </div>
      <div className="space-y-1.5">
        <Label>Assigned to</Label>
        <Pick value={v.assignee} onChange={(x) => set({ assignee: x })} testId={`onboard-${id}-assignee`} options={directory.map((p) => ({ value: p.id, label: p.name }))} />
      </div>
      <p className="sm:col-span-2 text-xs text-[#0F172A]/50">{svc.help}</p>
      <TaskPlanEditor service={id} templates={templates} roles={roles} directory={directory} defaultAssignee={defaultAssignee}
        value={{ template_id: v.template_id, task_plan: v.task_plan }} onChange={setPlan} testPrefix={`onboard-${id}`} />
    </div>
  );
}

const initial = (svc, assignee, templates, roles, deadlineRules) => {
  const tpl = templates.find((t) => t.service === svc.key);
  return {
    frequency: svc.frequency, period_end: svc.taxYear ? taxYearEnd(TAX_YEARS[2]) : "", tax_year: TAX_YEARS[2],
    ...(svc.taxYear ? datesFor(svc, taxYearEnd(TAX_YEARS[2]), deadlineRules?.[svc.key], tpl) : { due: "", statutory: "" }), duration: svc.duration, lead: svc.lead, assignee,
    ...(svc.taxYear && tpl?.start_rule ? { lead: datesFor(svc, taxYearEnd(TAX_YEARS[2]), deadlineRules?.[svc.key], tpl).lead } : {}),
    template_id: tpl?.id || null, task_plan: tpl ? planFromTemplate(tpl, assignee, roles) : [],
  };
};

export default function OnboardDialog({ open, client, directory, templates, roles, defaultAssignee, onClose, onSaved }) {
  const [sel, setSel] = useState({});
  const [rules, setRules] = useState({});
  useEffect(() => {
    api.get("/deadline-rules").then((r) => setRules(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (open) setSel({});
  }, [open]);

  const toggle = (svc) => setSel((p) => {
    const next = { ...p };
    if (next[svc.key]) delete next[svc.key];
    else next[svc.key] = initial(svc, defaultAssignee, templates, roles, rules);
    return next;
  });

  const submit = async (e) => {
    e.preventDefault();
    const jobs = SERVICES.filter((s) => sel[s.key]).map((s) => {
      const v = sel[s.key];
      return {
        client_id: client.id, service: s.key, title: s.title, category: s.category, priority: s.priority, frequency: v.frequency,
        duration_minutes: v.task_plan.length ? planTotal(v.task_plan) : Number(v.duration), lead_days: Number(v.lead), assignee_id: v.assignee,
        first_period_end: v.period_end, first_due: s.hideDue ? v.period_end : v.due, statutory_first_due: s.noStatutory ? null : v.statutory || null,
        template_id: v.template_id, task_plan: cleanPlan(v.task_plan),
      };
    });
    try {
      const { data } = await api.post(`/clients/${client.id}/onboard`, { jobs });
      toast.success(`${jobs.length} service${jobs.length > 1 ? "s" : ""} set up · ${data.job_cards_created} job card${data.job_cards_created === 1 ? "" : "s"} created`);
      onClose();
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const count = Object.keys(sel).length;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl bg-[#FDFBF7] border-[#103080]/10 sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="onboard-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-brand text-2xl text-[#103080]">Onboard services for {client?.name}</DialogTitle>
          <DialogDescription>Pick what you do for this client and answer a few questions. We'll create a job card for every deadline.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {SERVICES.map((svc) => (
            <div key={svc.key} className={`rounded-2xl border p-4 transition-colors duration-200 ${sel[svc.key] ? "border-[#0090D0] bg-[#0090D0]/[0.04]" : "border-[#103080]/10 bg-white/60"}`}>
              <button type="button" onClick={() => toggle(svc)} className="w-full flex items-center gap-3 text-left" data-testid={`onboard-service-${svc.key}`}>
                <span className={`w-5 h-5 rounded-md border flex items-center justify-center ${sel[svc.key] ? "bg-[#103080] border-[#103080] text-white" : "border-[#103080]/30"}`}>
                  {sel[svc.key] && <Check size={12} />}
                </span>
                <span className="font-semibold text-[#103080]">{svc.label}</span>
              </button>
              {sel[svc.key] && (
                <ServiceForm svc={svc} v={sel[svc.key]} rule={rules[svc.key]} directory={directory} templates={templates} roles={roles} defaultAssignee={defaultAssignee}
                  set={(patch) => setSel((p) => ({ ...p, [svc.key]: { ...p[svc.key], ...patch } }))} />
              )}
            </div>
          ))}
          <div className="flex justify-end pt-2">
            <button type="submit" disabled={!count} data-testid="onboard-save-button"
              className="rounded-full bg-[#103080] text-white font-medium px-7 h-11 text-sm hover:bg-[#0090D0] transition-colors duration-200 disabled:opacity-50">
              Set up {count || ""} service{count === 1 ? "" : "s"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
